import { Box, Container, Stack, Typography } from "@mui/material";
import { Outlet } from "react-router";
import { useTranslation } from "react-i18next";

import logoWhite from "../assets/logos/PROCare_logo_white.svg";
import illustration from "../assets/illustrations/signin_illustration.svg";

/**
 * The public shell: full-viewport brand gradient, a 65/35 split on desktop
 * (marketing copy + illustration on the left, card on the right), stacking to
 * card-first on phones. Re-implemented from the previous dashboard's sign-in
 * design — the only screen there that was built from a real mock.
 *
 * <Outlet />, not a hard-coded page: the old AuthLayout rendered the sign-in
 * page directly, so /activate and /reset-password both showed sign-in.
 */
export function AuthLayout() {
  const { t } = useTranslation(["auth", "common"]);

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        background: "linear-gradient(180deg, #3456FF 0%, #6934FF 100%)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          flexGrow: 1,
          py: { xs: 3, md: 5 },
          // The Stack below needs a flex parent to stretch; without this the
          // content bunches at the top and the gradient is mostly empty.
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack sx={{ flexGrow: 1 }} spacing={{ xs: 4, md: 6 }}>
          <Box
            component="img"
            src={logoWhite}
            alt="ProCare"
            sx={{ height: 32, alignSelf: "flex-start" }}
          />

          <Stack
            direction={{ xs: "column-reverse", md: "row" }}
            spacing={{ xs: 4, md: 6 }}
            alignItems="center"
            justifyContent="center"
            sx={{ flexGrow: 1 }}
          >
            <Stack
              spacing={3}
              sx={{ flex: { md: "0 1 58%" }, color: "common.white" }}
            >
              <Typography variant="h1" sx={{ color: "inherit" }}>
                {t("auth:headline")}
              </Typography>
              <Typography variant="body1" sx={{ color: "inherit", opacity: 0.9 }}>
                {t("auth:subheadline")}
              </Typography>
              <Box
                component="img"
                src={illustration}
                alt=""
                // Decorative: the headline above already carries the meaning.
                sx={{
                  display: { xs: "none", md: "block" },
                  width: "100%",
                  maxWidth: 460,
                }}
              />
            </Stack>

            <Box sx={{ flex: { md: "0 1 42%" }, width: "100%", maxWidth: 440 }}>
              <Outlet />
            </Box>
          </Stack>

          <Typography
            variant="caption"
            sx={{ color: "common.white", opacity: 0.8 }}
          >
            {t("common:footer.copyright", { year: new Date().getFullYear() })}
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}
